import _, { has, isEqual, join, keys, map, pickBy, get } from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import Select from 'react-select';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { withFbConsumer } from '@featbit/react-client-sdk';
import { fetchOperatorGroup, hideDialog, showDialog } from '../../../core/actions';
import { toString } from '../../../core/utils';
import InlineEditTable from '../../../core/components/InlineEditTable/InlineEditTable';
import { PageFilters, FilterPlacement } from '../../../core/components/PageFilters';
import InlineStatusCell from '../components/InlineStatusCell';
import { formatAmount } from '../../../utils/money-util';
import { SortDirection } from '../../../core/components/DataTableHeaders';
import PageActions from '../../../core/components/PageActions';
import { viewPermissionRefs } from '../../../core/components/UserGroupPermissions';
import * as utils from '../../../core/utils';
import * as toastMessages from '../../../core/toastMessages';
import { formatPhoneNumber } from '../../../utils/number-util';
import { hasRole, RoleTypes } from '../../../core/components/Permission';
import { fetchEmployees } from '../../employees/actions';
import { selectEmployees } from '../../employees/reducer';
import { selectCoreSiteId, selectSiteSettingsSite } from '../../settings/reducer';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import * as actions from '../actions';
import { addRepairItem } from '../../claimDetail/actions';
import {
  ACTIVE_RECLAMATION_STATUSES,
  CLAIM_COMPLETION_FILE_STATUSES,
  CLAIM_STATUS_DISPLAY_ORDER,
  CLOSED_RECLAMATION_STATUSES,
  DEFAULT_ESTIMATES_COUNT,
  LEGACY_CLAIM_STATUSES,
  ReclamationStatus,
  ReclamationType,
} from '../models';
import {
  selectFromDate,
  selectPage,
  selectPageSize,
  selectReclamationDamageOptions,
  selectReclamations,
  selectStatusFilter,
  selectTotal,
  selectTypeFilter,
  selectArchivedFilter,
  selectUntilDate,
  selectClaimFilterValues,
  selectReclamationInEdit,
} from '../reducer';
import { selectApiRequests } from '../../../store/apiRequestReducer';
import { selectOperatorGroup, selectRole, selectUser } from '../../../store/authReducer';
import './ReclamationsTable.scss';
import reclamationsPropTypes from './reclamationsPropTypes';
import { applyFilters, getDefaultFilterValues } from './filters';
import { buildReclamationColumns } from './reclamationTableConstants';
import * as dataHandlers from '../utils/reclamationDataHandlers';
import { shouldSyncList } from '../../../core/utils/optimisticListSync';
import { Drawer, DrawerContainer, DrawerControls } from '../../../core/components/Drawer';
import ClaimDrawerContent from '../components/ClaimDrawerContent';
import ReclamationFormWrapper from './ReclamationFormWrapper';
import {
  validateStatusTransition,
  getStatusEmailTemplate,
  getCompletionInfo,
} from '../utils/statusValidation';
import {
  transformReclamationForTable,
  getStatusCellRowData as getStatusCellData,
} from '../utils/reclamationTransformers';

/** @typedef {import('./types').ReclamationsProps} ReclamationsProps */

const CLAIM_STATUS_QUERY_VALUES = {
  [ReclamationStatus.Incomplete]: 'Incomplete',
  [ReclamationStatus.Open]: 'Open',
  [ReclamationStatus.Investigating]: 'Investigating',
  [ReclamationStatus.RepairApproved]: 'RepairApproved',
  [ReclamationStatus.RepairDenied]: 'RepairDenied',
  [ReclamationStatus.Resolved]: 'Resolved',
  [ReclamationStatus.FinalApproval]: 'FinalApproval',
  [ReclamationStatus.Completed]: 'Completed',
  [ReclamationStatus.Closed]: 'Closed',
};

const getReclamationUpdateToken = (reclamation) =>
  reclamation?.updatedAt ||
  reclamation?.updated_at ||
  reclamation?.modifiedAt ||
  reclamation?.modified_at;

const RECENT_REPAIR_DECISION_STATUSES = [
  ReclamationStatus.RepairApproved,
  ReclamationStatus.FinalApproval,
  ReclamationStatus.Resolved,
  ReclamationStatus.RepairDenied,
];

const RECENT_REPAIR_DECISION_VISIBLE_HOURS = 24;
const CLAIM_PAGE_SIZE = 50;

const arraysEqual = (a = [], b = []) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);

const toFilterDate = (value) => {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

const fromFilterDate = (date) => {
  if (!date) return '';
  return moment(date).format('YYYY-MM-DD');
};

const getStatusChangedAt = (reclamation) => {
  const history = Array.isArray(reclamation?.history) ? reclamation.history : [];
  const statusHistory = history.find((item) => {
    const details = item?.details || item?.activityDetails || {};
    return RECENT_REPAIR_DECISION_STATUSES.includes(Number(details.toStatus || item?.toStatus));
  });

  return (
    reclamation?._statusChangedAt ||
    reclamation?.statusChangedAt ||
    reclamation?.status_changed_at ||
    reclamation?.statusUpdatedAt ||
    reclamation?.status_updated_at ||
    statusHistory?.createdAt ||
    statusHistory?.created_at
  );
};

const isRecentRepairDecision = (reclamation) => {
  if (!RECENT_REPAIR_DECISION_STATUSES.includes(Number(reclamation?.status))) {
    return false;
  }

  const changedAt = getStatusChangedAt(reclamation);
  if (!changedAt) return false;

  const changedMoment = moment(changedAt);
  return (
    changedMoment.isValid() &&
    moment().diff(changedMoment, 'hours', true) < RECENT_REPAIR_DECISION_VISIBLE_HOURS
  );
};

/** @extends {Component<ReclamationsProps>} */
export class Reclamations extends Component {
  static propTypes = reclamationsPropTypes;

  constructor(props) {
    super(props);

    this.state = {
      searchResults: null,
      sortQuery:
        props.reclamationType === ReclamationType.Claim
          ? { key: 'createdAt', direction: SortDirection.DESC }
          : null,
      localReclamations: props.reclamations || [],
      drawerExpanded: false,
      selectedReclamation: null,
      drawerFocusSection: null,
      claimCreateDrawerExpanded: false,
      claimCreateDrawerMaximized: false,
      claimCreateDrawerMaximizedOffset: 0,
      statusEmailTemplate: null,
      statusEmailEstimatesCount: null,
      statusEmailDesignatedRepairShop: null,
      statusCustomerAction: null,
      claimDateRangeTouched: false,
    };
  }

  handleInlineDateSave = (id, ymd) => {
    const newDateIso = moment(ymd, 'YYYY-MM-DD').toISOString();
    const previous = this.getLocalReclamationById(id);
    this.updateLocalReclamation(id, { issueDate: newDateIso });
    const onError = (err) => {
      toastMessages.apiError(err);
      this.restoreLocalReclamation(previous);
    };
    this.props.patchReclamation({
      reclamation: { id, issueDate: newDateIso },
      callback: () => {},
      errorCallback: onError,
    });
  };

  handleInlineStatusSave = (id, status) => {
    const previous = this.getLocalReclamationById(id);

    if (
      previous &&
      Number(previous.status) === ReclamationStatus.Incomplete &&
      Number(status) === ReclamationStatus.Open
    ) {
      const site = this.props.match.params.site;
      this.props.showDialog('CONFIRM_DIALOG', {
        title: 'Finish claim?',
        text: 'This claim must be completed before it can move to Open. Would you like to finish it now?',
        submitButtonText: 'Finish Claim',
        onReady: () =>
          this.props.history.push({
            pathname: `/${site}/${this.props.basePath || 'claims'}/${id}/complete`,
            state: { initialReclamation: previous },
          }),
      });
      return;
    }

    const validation = this.hasUnrestrictedStatuses()
      ? { valid: true }
      : validateStatusTransition(previous, status, this.props.role);
    if (!validation.valid) {
      toastMessages.generateToastr('error', validation.error, validation.message);
      return;
    }

    if (Number(status) === ReclamationStatus.RepairApproved) {
      this.props.showDialog('ESTIMATES_REQUIRED', {
        canComposeEmail: this.canOpenClaimEmailComposer(),
        onSubmit: (estimates) => this.promptForStatusEmailChoice(id, status, { estimates }),
      });
      return;
    }

    this.promptForStatusEmailChoice(id, status);
  };

  // The old claims page has no status rules; claims v2 keeps them.
  hasUnrestrictedStatuses = () => this.props.basePath === 'claims-old';

  // Mirrors canSendEmail in ClaimDrawerContent: the drawer composer refuses to open
  // for anyone who cannot send claim emails.
  canOpenClaimEmailComposer = () =>
    hasRole(this.props.role, RoleTypes.SiteManager) && this.props.currentUser?.emailOptOut !== true;

  promptForStatusEmailChoice = (id, status, statusOptions = {}) => {
    const previous = this.getLocalReclamationById(id);
    const templateKey = getStatusEmailTemplate(previous, status, statusOptions.estimates);
    if (!templateKey) {
      this.applyInlineStatusSave(id, status, statusOptions);
      return;
    }

    // Only opt out of the backend email when the composer can actually take over.
    // Otherwise the status change would land with no email sent and no warning.
    if (!this.canOpenClaimEmailComposer()) {
      const lockedStatusKey = utils.toString(ReclamationStatus, Number(status));
      const lockedStatusLabel = this.props.intl.formatMessage({ id: lockedStatusKey });
      toastMessages.generateToastr(
        'warning',
        'Email not composed',
        `You do not have permission to compose claim emails, so the ${lockedStatusLabel} email was sent without site-specific details.`
      );
      this.applyInlineStatusSave(id, status, statusOptions);
      return;
    }

    if (Number(status) === ReclamationStatus.Resolved) {
      this.promptForResolvedCommunication(id, status, statusOptions);
      return;
    }

    // A designated repair shop or a non-default estimates count only reaches the guest through
    // the composer.
    const hasCustomEstimatesCount =
      statusOptions.estimates &&
      (Boolean(statusOptions.estimates.designatedRepairShop) ||
        statusOptions.estimates.estimatesCount !== DEFAULT_ESTIMATES_COUNT);
    const requiresTemplateInputs =
      hasCustomEstimatesCount ||
      ['EmailClaimResolved', 'EmailClaimFinalApproval', 'EmailClaimDenied'].includes(templateKey);
    if (requiresTemplateInputs) {
      return new Promise((resolve) =>
        this.applyInlineStatusSave(id, status, {
          ...statusOptions,
          openEmailComposer: true,
          onSettled: resolve,
        })
      );
    }

    const statusKey = utils.toString(ReclamationStatus, Number(status));
    const statusLabel = this.props.intl.formatMessage({ id: statusKey });
    this.props.showDialog('CONFIRM_DIALOG', {
      title: 'Add information to email?',
      text: `Would you like to add site-specific information before sending the ${statusLabel} email?`,
      submitButtonText: 'Add Information',
      cancelButtonText: 'Send Without Changes',
      // The cancel button closes itself via ModalDialog; the submit button has to
      // close explicitly or the dialog stays open over the drawer it just opened.
      onReady: () => {
        this.props.hideDialog();
        this.applyInlineStatusSave(id, status, {
          ...statusOptions,
          openEmailComposer: true,
        });
      },
      onCancel: () => this.applyInlineStatusSave(id, status, statusOptions),
    });
  };

  // Resolving a claim lets the user choose how to tell the guest. Either way the drawer opens
  // on that customer action and no automatic email is sent. Closing the dialog saves nothing.
  promptForResolvedCommunication = (id, status, statusOptions = {}) => {
    this.props.showDialog('CONFIRM_DIALOG', {
      title: 'Resolve claim',
      text: 'How would you like to communicate to the guest?',
      submitButtonText: 'Send Email',
      cancelButtonText: 'Phone Log',
      onReady: () => {
        this.props.hideDialog();
        this.applyInlineStatusSave(id, status, { ...statusOptions, openEmailComposer: true });
      },
      onCancel: () =>
        this.applyInlineStatusSave(id, status, { ...statusOptions, openPhoneLog: true }),
    });
  };

  applyInlineStatusSave = (id, status, options = {}) => {
    const previous = this.getLocalReclamationById(id);
    const payload = { id, status };
    const statusEmailTemplate = getStatusEmailTemplate(previous, status, options.estimates);
    const shouldOpenStatusEmailComposer = Boolean(options.openEmailComposer && statusEmailTemplate);
    const shouldOpenPhoneLog = Boolean(options.openPhoneLog);
    if (shouldOpenStatusEmailComposer || shouldOpenPhoneLog) {
      payload.skipClaimEmailDueToUserOptOut = true;
    }
    if (
      previous &&
      Number(previous.status) === ReclamationStatus.Incomplete &&
      Number(status) === ReclamationStatus.Open
    ) {
      const operatorGroup = this.props.operatorGroup;
      const operatorGroupDetails = operatorGroup?.details || {};
      payload.operatorGroup = operatorGroup
        ? {
            name: operatorGroup.name,
            details: {
              email: operatorGroupDetails.email || operatorGroup.email,
              emailSendingDomain:
                operatorGroupDetails.emailSendingDomain || operatorGroup.emailSendingDomain,
            },
          }
        : null;
      payload.venue = this.props.siteSettingsSite || previous.issueSite;
      payload.type = utils.toString(ReclamationType, ReclamationType.Claim);
      payload.customerName = previous.customerName;
      payload.customerEmail = previous.customerEmail;
      payload.customerPhone = previous.customerPhone;
      payload.vehicleDetails = previous.vehicleDetails;
    }
    const statusPatch = { status };
    if (
      Number(status) === ReclamationStatus.Investigating ||
      RECENT_REPAIR_DECISION_STATUSES.includes(Number(status))
    ) {
      statusPatch._statusChangedAt = new Date().toISOString();
    }
    this.updateLocalReclamation(id, statusPatch);
    const shouldPromptForCompletionFiles =
      previous &&
      previous.type === ReclamationType.Claim &&
      CLAIM_COMPLETION_FILE_STATUSES.includes(Number(status)) &&
      !CLAIM_COMPLETION_FILE_STATUSES.includes(Number(previous.status));
    const onError = (err) => {
      toastMessages.apiError(err);
      this.restoreLocalReclamation(previous);
      options.onSettled?.();
    };
    const emailNotificationsEnabled =
      this.props.flags?.['claim-email-notifications'] === 'true' ||
      this.props.flags?.['claim-email-notifications'] === true;
    this.props.patchReclamation({
      reclamation: payload,
      callback: () => {
        if (shouldOpenStatusEmailComposer) {
          this.setState({
            drawerExpanded: true,
            selectedReclamation: { ...previous, status },
            drawerFocusSection: null,
            statusEmailTemplate,
            statusEmailEstimatesCount: options.estimates?.estimatesCount ?? null,
            statusEmailDesignatedRepairShop: options.estimates?.designatedRepairShop ?? null,
            statusCustomerAction: null,
          });
          this.props.getReclamation(id);
        } else if (shouldOpenPhoneLog) {
          this.setState({
            drawerExpanded: true,
            selectedReclamation: { ...previous, status },
            drawerFocusSection: null,
            statusEmailTemplate: null,
            statusEmailEstimatesCount: null,
            statusEmailDesignatedRepairShop: null,
            statusCustomerAction: 'call',
          });
          this.props.getReclamation(id);
        } else if (statusEmailTemplate && previous?.customerEmail) {
          toastMessages.generateToastr(
            'success',
            'Email Sent',
            `Status update email sent to ${previous.customerEmail}`
          );
        }
        if (
          shouldPromptForCompletionFiles &&
          !shouldOpenStatusEmailComposer &&
          !shouldOpenPhoneLog
        ) {
          this.showCompletionFilesPrompt({ ...previous, status });
        }
        options.onSettled?.();
      },
      errorCallback: onError,
    });
  };

  getRowClassName = (row) => (row._recentlyApprovedOrDenied ? 'recently-approved-or-denied' : '');

  showCompletionFilesPrompt = (reclamation) => {
    this.props.showDialog('CONFIRM_DIALOG', {
      title: 'Add completion files?',
      text: 'The claim status was updated. Would you like to upload photos or videos now?',
      submitButtonText: 'Upload Files',
      onReady: () => {
        this.props.hideDialog();
        this.setState({
          drawerExpanded: true,
          selectedReclamation: reclamation,
          drawerFocusSection: 'media',
        });
        this.props.getReclamation(reclamation.id);
      },
    });
  };

  handleInlineTypeSave = (id, claimDamagePartId) => {
    const previous = this.getLocalReclamationById(id);
    this.updateLocalReclamation(id, { claimDamagePartId });
    const onError = (err) => {
      toastMessages.apiError(err);
      this.restoreLocalReclamation(previous);
    };
    this.props.patchReclamation({
      reclamation: { id, claimDamagePartId },
      callback: () => {},
      errorCallback: onError,
    });
  };

  handleInlineFieldSave = (id, field, value, isAmount = false, selectedCurrency = 'USD') => {
    const originalReclamation = this.props.reclamations.find((rec) => rec.id === id);
    const payload = { id };
    if (field === 'vehicle') {
      payload.vehicleDetails = { plate: value };
    } else if (field === 'cost' && isAmount) {
      const numeric = Number(value);
      payload.cost = Number.isFinite(numeric) ? `${numeric}` : '0';
      payload.currency = selectedCurrency || originalReclamation?.currency || 'USD';
      if (originalReclamation && Array.isArray(originalReclamation.repairItems)) {
        payload.repairItems = originalReclamation.repairItems.map((item, index) =>
          index === 0 ? { ...item, total: payload.cost } : item
        );
      } else {
        payload.repairItems = [{ total: payload.cost }];
      }
    } else {
      payload[field] = value;
    }
    const previous = this.getLocalReclamationById(id);
    this.updateLocalReclamation(id, payload);
    const onError = (err) => {
      toastMessages.apiError(err);
      this.restoreLocalReclamation(previous);
    };
    this.props.patchReclamation({
      reclamation: payload,
      callback: () => {},
      errorCallback: onError,
    });
  };

  getLocalReclamationById = (id) => {
    const source =
      this.state.searchResults || this.state.localReclamations || this.props.reclamations || [];
    const match = source.find((rec) => rec.id === id);
    if (!match && this.state.selectedReclamation?.id === id) {
      return {
        ...this.state.selectedReclamation,
        vehicleDetails: { ...(this.state.selectedReclamation.vehicleDetails || {}) },
      };
    }
    if (!match) return null;
    return {
      ...match,
      vehicleDetails: { ...(match.vehicleDetails || {}) },
    };
  };

  restoreLocalReclamation = (nextReclamation) => {
    if (!nextReclamation) return;
    const applyReplace = (rec) => (rec.id === nextReclamation.id ? nextReclamation : rec);
    this.setState((prev) => {
      const nextLocal = (prev.localReclamations || []).map(applyReplace);
      const nextSearch = prev.searchResults ? prev.searchResults.map(applyReplace) : null;
      return {
        ...prev,
        localReclamations: nextLocal,
        searchResults: nextSearch,
        selectedReclamation:
          prev.selectedReclamation?.id === nextReclamation.id
            ? nextReclamation
            : prev.selectedReclamation,
      };
    });
  };

  updateLocalReclamation = (id, patch) => {
    dataHandlers.updateLocalReclamation(this.setState.bind(this), id, patch);
    this.setState((prev) => {
      if (prev.selectedReclamation?.id !== id) return null;

      const selectedReclamation = { ...prev.selectedReclamation, ...patch };
      if (prev.selectedReclamation.vehicleDetails || patch.vehicleDetails) {
        selectedReclamation.vehicleDetails = {
          ...(prev.selectedReclamation.vehicleDetails || {}),
          ...(patch.vehicleDetails || {}),
        };
      }

      return { selectedReclamation };
    });
  };

  handleCellCommit = (payload) => {
    dataHandlers.handleCellCommit(payload, this.handleInlineFieldSave);
  };

  getClaimFilterValues = () => ({
    ...getDefaultFilterValues(),
    ...(this.props.claimFilterValues || {}),
    startDate: this.state.claimDateRangeTouched
      ? this.props.claimFilterValues?.startDate || ''
      : '',
  });

  updateClaimFilterValues = (patch) => {
    this.props.setClaimFilterValues({
      ...this.getClaimFilterValues(),
      ...patch,
    });
  };

  getClaimPageFilterConfigs = () => {
    const { intl } = this.props;
    const filterValues = this.getClaimFilterValues();
    const selectedStatusValues = Array.isArray(filterValues.statusFilter)
      ? filterValues.statusFilter
      : [];
    const selectedTypeValues = Array.isArray(filterValues.typeFilter)
      ? filterValues.typeFilter
      : [];
    const statusOptions = this.getStatusSelectOptions().map((option, index) => {
      const isGroup = Array.isArray(option.value);
      const value = isGroup ? `status-group-${index}` : `status-${option.value}`;
      return {
        value,
        text: option.label,
        selected: isGroup
          ? arraysEqual(option.value, selectedStatusValues)
          : selectedStatusValues.some(
              (statusValue) => Number(statusValue) === Number(option.value)
            ),
        rawValue: option.value,
      };
    });
    const typeOptions = this.getTypeSelectOptions().map((option) => ({
      value: String(option.value),
      text: option.label,
      selected: selectedTypeValues.some((typeValue) => Number(typeValue) === Number(option.value)),
    }));

    return [
      {
        id: 'dateRange',
        type: 'dateRange',
        name: intl.formatMessage({ id: 'date' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <i className="uil uil-calendar-alt" />,
        startDate: toFilterDate(filterValues.startDate),
        endDate: toFilterDate(filterValues.endDate),
        native: true,
        onChange: ([startDate, endDate]) => {
          this.setState({ claimDateRangeTouched: true });
          this.updateClaimFilterValues({
            startDate: fromFilterDate(startDate),
            endDate: fromFilterDate(endDate),
          });
        },
      },
      {
        id: 'statusFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'status' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <i className="uil uil-check-circle" />,
        options: statusOptions,
        onChange: (values) => {
          const nextStatuses = values.flatMap((value) => {
            const option = statusOptions.find((item) => item.value === value);
            if (!option) return [];
            return Array.isArray(option.rawValue) ? option.rawValue : [option.rawValue];
          });
          this.updateClaimFilterValues({ statusFilter: nextStatuses });
        },
      },
      {
        id: 'typeFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'type' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <i className="uil uil-tag-alt" />,
        options: typeOptions,
        onChange: (values) =>
          this.updateClaimFilterValues({
            typeFilter: values.map((value) => Number(value)),
          }),
      },
      {
        id: 'vehicleFilter',
        type: 'search',
        name: intl.formatMessage({ id: 'vehicle' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <i className="uil uil-car" />,
        searchTerm: filterValues.vehicleFilter || '',
        onChange: (value) => this.updateClaimFilterValues({ vehicleFilter: value || '' }),
      },
      {
        id: 'guestFilter',
        type: 'search',
        name: intl.formatMessage({ id: 'customerName' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <i className="uil uil-user" />,
        searchTerm: filterValues.guestFilter || '',
        onChange: (value) => this.updateClaimFilterValues({ guestFilter: value || '' }),
      },
      {
        id: 'phoneFilter',
        type: 'search',
        name: intl.formatMessage({ id: 'customerPhoneNumber' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <i className="uil uil-phone" />,
        searchTerm: filterValues.phoneFilter || '',
        onChange: (value) => this.updateClaimFilterValues({ phoneFilter: value || '' }),
      },
      {
        id: 'archivedFilter',
        type: 'select',
        name: intl.formatMessage({ id: 'includeArchived' }),
        placement: FilterPlacement.Menu,
        defaultVisible: true,
        icon: <i className="uil uil-archive" />,
        multiSelect: false,
        options: [
          {
            value: 'includeArchived',
            text: intl.formatMessage({ id: 'includeArchived' }),
            selected: Boolean(this.props.archivedFilter),
          },
        ],
        onChange: (values) => this.props.setArchivedFilter(values.includes('includeArchived')),
      },
    ];
  };

  handleSort = (keyName) => {
    dataHandlers.handleSort(this.setState.bind(this), this.state.sortQuery, keyName);
  };

  handleOpenDrawer = (reclamation) => {
    this.setState({
      drawerExpanded: true,
      selectedReclamation: reclamation,
      drawerFocusSection: null,
      statusEmailTemplate: null,
      statusEmailEstimatesCount: null,
      statusEmailDesignatedRepairShop: null,
      statusCustomerAction: null,
    });

    this.props.getReclamation(reclamation.id);
  };

  handleCloseDrawer = () => {
    this.setState({
      drawerExpanded: false,
      selectedReclamation: null,
      drawerFocusSection: null,
      statusEmailTemplate: null,
      statusEmailEstimatesCount: null,
      statusEmailDesignatedRepairShop: null,
      statusCustomerAction: null,
    });
  };

  handleOpenClaimCreateDrawer = () => {
    this.setState({
      drawerExpanded: false,
      selectedReclamation: null,
      drawerFocusSection: null,
      claimCreateDrawerExpanded: true,
      claimCreateDrawerMaximized: true,
    });
  };

  handleCloseClaimCreateDrawer = () => {
    this.setState({
      claimCreateDrawerExpanded: false,
      claimCreateDrawerMaximized: false,
    });
  };

  handleClaimCreated = () => {
    this.handleCloseClaimCreateDrawer();
    this.fetchReclamations();
  };

  handleMaximizeClaimCreateDrawer = () => {
    this.setState((prevState) => ({
      claimCreateDrawerMaximized: !prevState.claimCreateDrawerMaximized,
    }));
  };

  getSideNavWidth = () => {
    if (window.innerWidth <= 700) return 0;
    const nav = document.querySelector('nav.main');
    return nav ? Math.round(nav.getBoundingClientRect().width) : 0;
  };

  updateClaimCreateDrawerMaximizedOffset = () => {
    const nextOffset = this.getSideNavWidth();
    if (nextOffset !== this.state.claimCreateDrawerMaximizedOffset) {
      this.setState({ claimCreateDrawerMaximizedOffset: nextOffset });
    }
  };

  observeSideNavWidth = () => {
    window.addEventListener('resize', this.updateClaimCreateDrawerMaximizedOffset);
    this.updateClaimCreateDrawerMaximizedOffset();

    const nav = document.querySelector('nav.main');
    if (nav && window.ResizeObserver) {
      this.sideNavResizeObserver = new window.ResizeObserver(
        this.updateClaimCreateDrawerMaximizedOffset
      );
      this.sideNavResizeObserver.observe(nav);
    }
  };

  fetchReclamations = () => {
    const query = this.getQuery();
    this.props.getReclamations(query);
  };

  componentDidMount() {
    this.props.getDamageClaimOptions();
    this.ensureDefaultDateRangeForStatus();
    this.fetchReclamations();
    this.props.fetchEmployees();
    this.props.fetchOperatorGroup();
    this.observeSideNavWidth();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateClaimCreateDrawerMaximizedOffset);
    if (this.sideNavResizeObserver) {
      this.sideNavResizeObserver.disconnect();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.location?.state?.openDrawerId &&
      this.props.reclamations &&
      this.props.reclamations.length > 0 &&
      !this.state.drawerExpanded
    ) {
      const reclamationToOpen = this.props.reclamations.find(
        (r) => r.id === this.props.location.state.openDrawerId
      );
      if (reclamationToOpen) {
        this.handleOpenDrawer(reclamationToOpen);
        this.props.history.replace({ ...this.props.location, state: {} });
      }
    }

    if (this.state.selectedReclamation && this.props.reclamations !== prevProps.reclamations) {
      const updatedReclamation = this.props.reclamations.find(
        (r) => r.id === this.state.selectedReclamation.id
      );
      const nextUpdateToken = getReclamationUpdateToken(updatedReclamation);
      const selectedUpdateToken = getReclamationUpdateToken(this.state.selectedReclamation);
      if (updatedReclamation && nextUpdateToken && nextUpdateToken !== selectedUpdateToken) {
        this.setState({ selectedReclamation: updatedReclamation });
      }
    }
    if (
      this.state.selectedReclamation &&
      this.props.reclamationInEdit &&
      this.props.reclamationInEdit.id === this.state.selectedReclamation.id &&
      this.props.reclamationInEdit !== this.state.selectedReclamation
    ) {
      this.setState({ selectedReclamation: this.props.reclamationInEdit });
    }

    const datesChanged = !isEqual(prevProps.dateQuery, this.props.dateQuery);
    const sortQueryChanged = !isEqual(prevState.sortQuery, this.state.sortQuery);
    const filterChanged =
      prevProps.statusFilter !== this.props.statusFilter ||
      prevProps.archivedFilter !== this.props.archivedFilter ||
      prevProps.typeFilter !== this.props.typeFilter ||
      !isEqual(prevProps.claimFilterValues, this.props.claimFilterValues);
    const primarySiteChange = !isEqual(prevProps.siteId, this.props.siteId);
    const multiSiteSelectionChange = !isEqual(prevProps.multiSites, this.props.multiSites);

    if (prevProps.statusFilter !== this.props.statusFilter) {
      const changed = this.ensureDefaultDateRangeForStatus();
      if (changed) {
        return;
      }
    }

    if (
      filterChanged ||
      datesChanged ||
      primarySiteChange ||
      multiSiteSelectionChange ||
      sortQueryChanged
    ) {
      this.fetchReclamations();
    }

    // Smart sync: only update local state when server data actually changes
    this.syncLocalReclamations(prevProps.reclamations, this.props.reclamations);
  }

  syncLocalReclamations = (prevReclamations, nextReclamations) => {
    if (isEqual(prevReclamations, nextReclamations)) return;

    const compareFields = (item, local) => {
      if (item.status !== local.status) return true;
      if (item.issueDate !== local.issueDate) return true;
      if (item.claimDamagePartId !== local.claimDamagePartId) return true;
      if (item.customerName !== local.customerName) return true;
      if (item.customerEmail !== local.customerEmail) return true;
      if (item.customerPhone !== local.customerPhone) return true;
      if (item.cost !== local.cost) return true;
      if (!isEqual(item.vehicleDetails, local.vehicleDetails)) return true;
      return false;
    };

    if (shouldSyncList(nextReclamations, this.state.localReclamations, { compareFields })) {
      this.setState({ localReclamations: nextReclamations || [] });
    }
  };

  ensureDefaultDateRangeForStatus = () => {
    const closedStatuses = [...CLOSED_RECLAMATION_STATUSES, ReclamationStatus.AllClosed];
    const isClosed = closedStatuses.includes(Number(this.props.statusFilter));

    const desiredFrom = isClosed
      ? moment().clone().subtract(1, 'year')
      : moment().clone().startOf('year');
    const desiredUntil = moment();

    const sameFrom = this.props.fromDate && this.props.fromDate.isSame(desiredFrom, 'day');
    const sameUntil = this.props.untilDate && this.props.untilDate.isSame(desiredUntil, 'day');
    if (!sameFrom) this.props.setFromDate(desiredFrom);
    if (!sameUntil) this.props.setUntilDate(desiredUntil);
    return !sameFrom || !sameUntil;
  };

  getReclamationsPage = (page) => {
    this.props.getReclamations({ page, ...this.getQuery() });
  };

  getQuery = () => {
    const {
      vehicleFilter = '',
      guestFilter = '',
      phoneFilter = '',
      statusFilter: barStatusFilter = [],
      typeFilter: barTypeFilter = [],
      startDate = '',
      endDate = '',
    } = this.getClaimFilterValues();

    const query = {
      types: utils.toString(ReclamationType, this.props.reclamationType),
      pageSize: CLAIM_PAGE_SIZE,
      ...this.props.dateQuery,
    };

    if (startDate) {
      query.fromDate = moment(startDate).format('YYYY-MM-DD');
    }
    if (endDate) {
      query.untilDate = moment(endDate).format('YYYY-MM-DD');
    }

    if (this.props.reclamationType === ReclamationType.Claim) {
      if (Array.isArray(barStatusFilter) && barStatusFilter.length > 0) {
        query.statuses = barStatusFilter.map((v) => utils.toString(ReclamationStatus, Number(v)));
      } else {
        query.statuses = this.getStatusInCorrectFormat();
      }

      if (Array.isArray(barTypeFilter) && barTypeFilter.length > 0) {
        query.type = barTypeFilter;
      } else {
        query.type = this.props.typeFilter;
      }

      if (this.props.archivedFilter) {
        query.includeArchived = this.props.archivedFilter;
      }
      if (vehicleFilter.trim()) {
        query.vehiclePlate = vehicleFilter.trim();
      }
      if (guestFilter.trim()) {
        query.customerName = guestFilter.trim();
      }
      if (phoneFilter.trim()) {
        query.customerPhone = phoneFilter.replace(/\D/g, '');
      }
      if (this.state.sortQuery) {
        if (this.state.sortQuery.key) {
          query.key = this.state.sortQuery.key;
        }
        if (!_.isNil(this.state.sortQuery.direction)) {
          query.direction = toString(SortDirection, this.state.sortQuery.direction);
        }
      }
    }

    Object.keys(query).forEach((key) => {
      if (query[key] === 0) {
        delete query[key];
      }
    });

    return query;
  };

  setSearchMatch = (searchResults, searchResultsDetails, searchText) => {
    this.setState({
      searchResults,
    });
  };

  getStatusInCorrectFormat = () => {
    if (
      this.props.statusFilter === ReclamationStatus.AllOpen ||
      this.props.statusFilter === ReclamationStatus.AllClosed ||
      this.props.statusFilter === ReclamationStatus.All
    ) {
      return this.formatStatusFilterIntoArray(this.props.statusFilter);
    }

    return CLAIM_STATUS_QUERY_VALUES[this.props.statusFilter];
  };

  getPaginationInfo() {
    return {
      total: this.props.total,
      page: this.props.page,
      pageSize: CLAIM_PAGE_SIZE,
      goToPage: this.getReclamationsPage,
    };
  }

  getStatusSelectOptions = () => {
    const { intl } = this.props;
    const groupOptions = [
      {
        value: [...ACTIVE_RECLAMATION_STATUSES, ...CLOSED_RECLAMATION_STATUSES],
        label: intl.formatMessage({ id: 'All' }),
      },
      { value: [...ACTIVE_RECLAMATION_STATUSES], label: intl.formatMessage({ id: 'AllOpen' }) },
      { value: [...CLOSED_RECLAMATION_STATUSES], label: intl.formatMessage({ id: 'AllClosed' }) },
    ];
    const individualOptions = this.getStatusInlineEditOptions().filter(
      (option) => !LEGACY_CLAIM_STATUSES.includes(option.value)
    );
    return [...groupOptions, ...individualOptions];
  };

  // Legacy statuses are included so rows still in them get a label; the cell only
  // offers the row's allowed statuses.
  getStatusInlineEditOptions = () => {
    const { intl } = this.props;
    return [...CLAIM_STATUS_DISPLAY_ORDER, ...LEGACY_CLAIM_STATUSES].map((value) => ({
      value,
      label: intl.formatMessage({ id: utils.toString(ReclamationStatus, value) }),
    }));
  };

  getStatusCellRowData = (reclamation) => {
    if (!reclamation) return null;
    return getStatusCellData(
      reclamation,
      this.props.role,
      this.props.intl,
      this.hasUnrestrictedStatuses()
    );
  };

  getTypeSelectOptions = () => {
    return (this.props.damageClaimOptions || []).map((option, index) => ({
      value: option.id,
      label: option.value,
      styleIndex: index,
    }));
  };

  getRewashReason = (reclamation) => {
    if (!reclamation.incidentDetails || !reclamation.incidentDetails.needsRewash) {
      return '';
    }
    const rewashReasons = keys(pickBy(reclamation.incidentDetails.needsRewash));
    return join(
      map(rewashReasons, (reasonName) => this.props.intl.formatMessage({ id: reasonName })),
      ', '
    );
  };

  getDatatableDatum = (reclamation) => {
    return transformReclamationForTable(reclamation, {
      reclamationType: this.props.reclamationType,
      damageClaimOptions: this.props.damageClaimOptions,
      userRole: this.props.role,
      intl: this.props.intl,
      getRewashReason: this.getRewashReason,
      unrestrictedStatuses: this.hasUnrestrictedStatuses(),
    });
  };

  formatStatusFilterIntoArray = (filter) => {
    const activeStatuses = ACTIVE_RECLAMATION_STATUSES.map(
      (status) => CLAIM_STATUS_QUERY_VALUES[status]
    );
    const closedStatuses = CLOSED_RECLAMATION_STATUSES.map(
      (status) => CLAIM_STATUS_QUERY_VALUES[status]
    );
    if (filter === ReclamationStatus.All) {
      return [...activeStatuses, ...closedStatuses];
    }
    if (filter === ReclamationStatus.AllOpen) {
      return activeStatuses;
    }
    return closedStatuses;
  };

  shouldRenderInCurrentStatusView = (reclamation) => {
    return Boolean(reclamation);
  };

  editReclamation = (event, { _id }) => {
    const path = this.props.history.location.pathname;
    this.props.history.push(`${path}/${_id}/edit`);
  };

  openReclamation = (row) => {
    const path = this.props.history.location.pathname;
    const id = row?._id || row?.id;
    if (!id) return;
    this.props.history.push(`${path}/${id}`);
  };

  deleteReclamation = (event, { _id }) => {
    const deletionParams = {
      id: _id,
      callback: () => this.fetchReclamations(),
    };

    const confirmProps = {
      onReady: this.props.deleteReclamation.bind(null, deletionParams),
      text: `${this.props.intl.formatMessage({
        id: 'confirmReclamationDelete',
      })}: ${_id}?`,
      title: this.props.intl.formatMessage({ id: 'deleteReclamation' }),
    };

    this.props.showDialog('CONFIRM_DIALOG', confirmProps);
  };

  getTableData = () => {
    const baseData =
      this.state.searchResults || this.state.localReclamations || this.props.reclamations;
    const statusViewData = baseData.filter(this.shouldRenderInCurrentStatusView);
    const filtered = applyFilters(statusViewData, this.getClaimFilterValues());
    return filtered.map(this.getDatatableDatum);
  };

  initializeTableColumns = () => {
    return buildReclamationColumns({
      intl: this.props.intl,
      reclamationType: this.props.reclamationType,
      statusOptions: this.getStatusInlineEditOptions(),
      typeOptions: this.getTypeSelectOptions(),
      onSaveDate: this.handleInlineDateSave,
      onSaveStatus: this.handleInlineStatusSave,
      onSaveType: this.handleInlineTypeSave,
      onSaveField: this.handleInlineFieldSave,
      formatPhoneNumber,
      onDelete: this.deleteReclamation,
      onEdit: this.editReclamation,
    });
  };

  goToCreatePage = () => {
    if (this.props.basePath) {
      const id = this.props.match.params.site;
      this.props.history.push(`/${id}/${this.props.basePath}/create`);
      return;
    }
    if (this.props.reclamationType === ReclamationType.Claim) {
      this.handleOpenClaimCreateDrawer();
      return;
    }
    const type = this.props.reclamationType ? 'rewashes' : 'claims';
    const id = this.props.match.params.site;
    this.props.history.push(`/${id}/${type}/create`);
  };

  getButtonText = () => {
    const type = this.props.reclamationType;

    if (type === ReclamationType.Rewash) {
      return this.props.intl.formatMessage({ id: 'addRewash' });
    }

    return this.props.intl.formatMessage({ id: 'addClaim' });
  };

  render() {
    const sortState =
      this.state.sortQuery && this.state.sortQuery.key
        ? {
            keyName: this.state.sortQuery.key,
            direction: this.state.sortQuery.direction ? 'asc' : 'desc',
          }
        : undefined;
    const {
      selectedReclamation,
      drawerExpanded,
      claimCreateDrawerExpanded,
      claimCreateDrawerMaximized,
      claimCreateDrawerMaximizedOffset,
      statusEmailTemplate,
      statusEmailEstimatesCount,
      statusEmailDesignatedRepairShop,
      statusCustomerAction,
    } = this.state;
    const drawerReclamation =
      this.props.reclamationInEdit &&
      selectedReclamation &&
      this.props.reclamationInEdit.id === selectedReclamation.id
        ? this.props.reclamationInEdit
        : selectedReclamation;
    const pageActions = (
      <PageActions
        actionFunction={this.goToCreatePage}
        actions={[
          {
            text: this.getButtonText(),
            permissionKey: viewPermissionRefs.warranty,
            isPrimary: true,
          },
        ]}
      />
    );

    return (
      <section className="reclamations">
        <DrawerContainer>
          <div style={{ overflow: 'visible' }}>
            <div className="filters-actions-container mb-3">
              <PageFilters filters={this.getClaimPageFilterConfigs()} maxChicletFilters={4} />
              <div className="page-actions">{pageActions}</div>
            </div>
            <InlineEditTable
              api=""
              columns={this.initializeTableColumns()}
              rows={this.getTableData()}
              paginationInfo={this.getPaginationInfo()}
              isLoading={this.props.isLoading}
              onSort={this.handleSort}
              sortState={sortState}
              paginateLocally={false}
              onCellCommit={this.handleCellCommit}
              onRowClick={(row) => {
                if (this.props.basePath) {
                  const site = this.props.match.params.site;
                  this.props.history.push(`/${site}/${this.props.basePath}/${row._id}`);
                  return;
                }
                const reclamation = this.state.localReclamations.find((r) => r.id === row._id);
                if (reclamation) {
                  this.handleOpenDrawer(reclamation);
                }
              }}
              rowClassName={this.getRowClassName}
            />
          </div>
          <Drawer
            expanded={drawerExpanded}
            expandedWidth={50}
            onBackdropClick={this.handleCloseDrawer}
            overlay
          >
            <div className="flex space-between">
              <div>
                <DrawerControls closeFunction={this.handleCloseDrawer} maximized={false} />
              </div>
              {selectedReclamation && (
                <div className="align-right">
                  <button
                    className="button primary inverted mr-2 ml-2"
                    onClick={() => {
                      this.props.showDialog('DELETE_RECLAMATION', {
                        reclamation: selectedReclamation,
                        onConfirm: () => {
                          this.props.deleteReclamation(selectedReclamation.id);
                          this.handleCloseDrawer();
                        },
                      });
                    }}
                  >
                    {this.props.intl.formatMessage({ id: 'delete' })}
                  </button>
                  <button
                    className="button primary"
                    onClick={() => {
                      const type =
                        this.props.basePath ||
                        (selectedReclamation.type === ReclamationType.Claim
                          ? 'claims'
                          : 'rewashes');
                      const site = this.props.match.params.site;
                      const isIncompleteClaim =
                        selectedReclamation.type === ReclamationType.Claim &&
                        Number(selectedReclamation.status) === ReclamationStatus.Incomplete;
                      const action = isIncompleteClaim ? 'complete' : 'edit';
                      const pathname = `/${site}/${type}/${selectedReclamation.id}/${action}`;
                      this.props.history.push(
                        isIncompleteClaim
                          ? {
                              pathname,
                              state: { initialReclamation: selectedReclamation },
                            }
                          : pathname
                      );
                    }}
                  >
                    {selectedReclamation.type === ReclamationType.Claim &&
                    Number(selectedReclamation.status) === ReclamationStatus.Incomplete
                      ? 'Complete Claim'
                      : this.props.intl.formatMessage({ id: 'edit' })}
                  </button>
                </div>
              )}
            </div>
            <hr />
            {drawerReclamation && (
              <div
                className="reclamation-details"
                style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(100vh - 150px)' }}
              >
                <div className="drawer-header" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                  <div>
                    <strong>Claim #{drawerReclamation.id}</strong>
                  </div>
                  <InlineStatusCell
                    rowData={this.getStatusCellRowData(drawerReclamation)}
                    mixIns={{
                      onSaveStatus: this.handleInlineStatusSave,
                      statusOptions: this.getStatusInlineEditOptions(),
                      statusDropdownAlign: 'right',
                      statusDropdownMinWidth: 340,
                    }}
                  />
                </div>
                <ClaimDrawerContent
                  reclamation={drawerReclamation}
                  employees={this.props.employees}
                  damageClaimOptions={this.props.damageClaimOptions}
                  addFile={this.props.addFile}
                  addReclamationNote={this.props.addReclamationNote}
                  deleteFile={this.props.deleteFile}
                  showDialog={this.props.showDialog}
                  patchReclamation={this.props.patchReclamation}
                  sendReclamationEmail={this.props.sendReclamationEmail}
                  getReclamation={this.props.getReclamation}
                  operatorGroup={this.props.operatorGroup}
                  role={this.props.role}
                  currentUser={this.props.currentUser}
                  venue={selectedReclamation?.issueSite}
                  intl={this.props.intl}
                  scrollToMediaSection={this.state.drawerFocusSection === 'media'}
                  onMediaSectionFocused={() => this.setState({ drawerFocusSection: null })}
                  initialEmailTemplate={statusEmailTemplate}
                  initialEstimatesCount={statusEmailEstimatesCount}
                  initialDesignatedRepairShop={statusEmailDesignatedRepairShop}
                  initialCustomerAction={statusCustomerAction}
                  // The old claims page hides customer actions, except when a status change
                  // opened the drawer to email or call the guest.
                  hideCustomerActions={
                    !!this.props.basePath && !statusEmailTemplate && !statusCustomerAction
                  }
                />
              </div>
            )}
          </Drawer>
          <Drawer
            expanded={claimCreateDrawerExpanded}
            maximized={claimCreateDrawerMaximized}
            maximizedOffset={claimCreateDrawerMaximizedOffset}
            expandedWidth={70}
            overlay
          >
            {claimCreateDrawerExpanded && (
              <div className="claim-wizard-drawer-content">
                <ReclamationFormWrapper
                  useInternalDrawer={false}
                  drawerMaximized={claimCreateDrawerMaximized}
                  onMaximizeDrawer={this.handleMaximizeClaimCreateDrawer}
                  onCloseDrawer={this.handleCloseClaimCreateDrawer}
                  onClaimCreated={this.handleClaimCreated}
                />
              </div>
            )}
          </Drawer>
        </DrawerContainer>
      </section>
    );
  }
}

const mapStateToProps = (state) => {
  const fromDate = selectFromDate(state);
  const untilDate = selectUntilDate(state);
  const apiRequests = selectApiRequests(state);
  const isLoading = get(apiRequests, `${actions.GET_RECLAMATIONS}.isLoading`, false);

  return {
    reclamations: selectReclamations(state),
    reclamationInEdit: selectReclamationInEdit(state),
    employees: selectEmployees(state),
    multiSites: selectMultiSiteSelection(state),
    siteId: selectCoreSiteId(state),
    total: selectTotal(state),
    page: selectPage(state),
    pageSize: selectPageSize(state),
    fromDate,
    untilDate,
    operatorGroup: selectOperatorGroup(state),
    role: selectRole(state),
    currentUser: selectUser(state),
    siteSettingsSite: selectSiteSettingsSite(state),
    statusFilter: selectStatusFilter(state),
    typeFilter: selectTypeFilter(state),
    archivedFilter: selectArchivedFilter(state),
    dateQuery: {
      fromDate: fromDate.format('YYYY-MM-DD'),
      untilDate: untilDate.format('YYYY-MM-DD'),
    },
    damageClaimOptions: selectReclamationDamageOptions(state),
    claimFilterValues: selectClaimFilterValues(state),
    operatorGroup: selectOperatorGroup(state),
    role: selectRole(state),
    currentUser: selectUser(state),
    isLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      ...actions,
      fetchEmployees,
      fetchOperatorGroup,
      showDialog,
      hideDialog,
      addRepairItem,
    },
    dispatch
  );
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withRouter,
  withFbConsumer()
)(Reclamations);
